import React, { useEffect, useMemo, useState } from 'react';
import { Paperclip, Sparkles, FileText, ChevronDown, Trash2, Link2, Check, X, CornerUpLeft } from 'lucide-react';
import { ExecInboxThread, ExecInboxStage, EXEC_INBOX_UNSORTED_STAGE_ID } from '../../types';
import type { LinkCandidate } from './ExecThreadPanel';

interface Props {
  threads: ExecInboxThread[];
  stages: ExecInboxStage[];
  selectedThreadId?: string;
  loading: boolean;
  totalMailboxThreads?: number;
  ownerEmail?: string;
  ownerName?: string;
  linkCandidates: LinkCandidate[];
  onOpen: (thread: ExecInboxThread) => void;
  onMove: (thread: ExecInboxThread, stageId: string) => void;
  onDelete?: (thread: ExecInboxThread) => void;
  onLink: (thread: ExecInboxThread, candidate: LinkCandidate | null) => void;
  onAcceptSuggestion: (thread: ExecInboxThread) => void;
  onDismissSuggestion: (thread: ExecInboxThread) => void;
}

function fmtTime(d: Date): string {
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return d.toLocaleDateString([], { month: 'numeric', day: 'numeric', year: '2-digit' });
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('') || '?';
}

function partyName(p: { email: string; name?: string }): string {
  return p.name?.trim() || p.email.split('@')[0];
}

function decodeEntities(s: string): string {
  return s
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function emailLocalLabel(email: string): string {
  const local = email.split('@')[0] || email;
  return local
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** Guess addressees from "Wade, Thanks…" or "Wade and Garrett, …" when mirror metadata hasn't resynced. */
function snippetAddressees(snippet: string): string[] {
  const s = decodeEntities(snippet).trim();
  const single = s.match(/^([A-Za-z][A-Za-z .'-]{0,40}),/);
  if (single) return [single[1].trim()];
  const multi = s.match(/^([A-Za-z][A-Za-z .'-]+(?:\s+and\s+[A-Za-z][A-Za-z .'-]+)+),/i);
  if (multi) {
    return multi[1].split(/\s+and\s+/i).map((n) => n.trim()).filter(Boolean);
  }
  return [];
}

interface ThreadRowPeople {
  sender: { label: string; isYou: boolean; title: string };
  to: string[];
  cc: string[];
}

/** Latest sender (always from the From header) + To/Cc on the same line. */
function threadRowPeople(t: ExecInboxThread, ownerEmail?: string, ownerName?: string): ThreadRowPeople {
  const owner = ownerEmail?.trim().toLowerCase() || '';
  const fromEmail = (t.lastFrom || '').trim().toLowerCase();
  const isYou = t.lastFromIsOwner || (!!owner && fromEmail === owner);

  const sender = isYou
    ? {
        label: 'You',
        isYou: true,
        title: ownerName
          ? `${ownerName} <${owner || t.lastFrom}>`
          : (owner ? `You <${owner}>` : 'You sent the latest message'),
      }
    : {
        label: t.lastFromName?.trim() || (fromEmail ? emailLocalLabel(t.lastFrom) : t.lastFrom) || '—',
        isYou: false,
        title: t.lastFromName ? `${t.lastFromName} <${t.lastFrom}>` : (t.lastFrom || ''),
      };

  const senderKey = sender.label.toLowerCase();
  const uniq = (names: string[]) => {
    const seen = new Set<string>();
    return names.filter((n) => {
      const k = n.toLowerCase();
      if (!k || seen.has(k) || k === senderKey) return false;
      seen.add(k);
      return true;
    });
  };

  let to = uniq((t.primaryRecipients || []).map(partyName).filter(Boolean));
  let cc = uniq((t.ccRecipients || []).map(partyName).filter(Boolean));

  const fallbackPool = uniq([
    ...snippetAddressees(t.snippet),
    ...(t.counterparts || []).map(partyName),
    ...(t.participants || [])
      .filter((e) => e.toLowerCase() !== owner)
      .map((e) => emailLocalLabel(e)),
  ].filter(Boolean));

  if (!to.length && fallbackPool.length) {
    to = isYou ? fallbackPool : fallbackPool.filter((n) => n.toLowerCase() !== senderKey);
  }

  // Inbound with no To metadata: the sender is the correspondent; no need to duplicate in To.
  if (!isYou && !to.length && !cc.length) {
    to = [];
  }

  return { sender, to: to.slice(0, 5), cc: cc.slice(0, 4) };
}

/** Show agent summary only — never the raw Gmail snippet in the list. */
function displaySummary(t: ExecInboxThread): { text: string; pending: boolean } {
  const s = t.agentSummary;
  const fresh = !!s?.text && (s.forMessageCount == null || s.forMessageCount >= t.messageCount);
  if (fresh && s) return { text: s.text, pending: false };
  return { text: 'Summary pending — inbox agent will summarize on next triage pass.', pending: true };
}

const ExecInboxList: React.FC<Props> = ({
  threads, stages, selectedThreadId, loading, totalMailboxThreads = 0, ownerEmail, ownerName, linkCandidates,
  onOpen, onMove, onDelete, onLink, onAcceptSuggestion, onDismissSuggestion,
}) => {
  const [quickMove, setQuickMove] = useState<{ thread: ExecInboxThread; x: number; y: number } | null>(null);
  const [quickLink, setQuickLink] = useState<{ thread: ExecInboxThread; x: number; y: number } | null>(null);
  const [linkQuery, setLinkQuery] = useState('');

  useEffect(() => {
    if (!quickMove && !quickLink) return;
    const close = () => { setQuickMove(null); setQuickLink(null); setLinkQuery(''); };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); };
  }, [quickMove, quickLink]);

  const stageMeta = (id: string): { label: string; accent: string } => {
    if (id === EXEC_INBOX_UNSORTED_STAGE_ID) return { label: 'Unsorted', accent: '#94A3B8' };
    const s = stages.find((x) => x.id === id);
    return s ? { label: s.label, accent: s.accent } : { label: id, accent: '#94A3B8' };
  };

  const filteredCandidates = useMemo(() => {
    const q = linkQuery.trim().toLowerCase();
    if (!q) return linkCandidates.slice(0, 8);
    return linkCandidates.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [linkQuery, linkCandidates]);

  if (loading) {
    return (
      <div className="exec-list-wrap">
        {Array.from({ length: 8 }).map((_, i) => <div key={i} className="exec-skeleton-row" />)}
      </div>
    );
  }

  if (!threads.length) {
    return (
      <div className="exec-empty">
        <h3>Nothing here</h3>
        {totalMailboxThreads > 0
          ? <p>No threads in this stage. Check the other funnel stages in the sidebar — you have {totalMailboxThreads} synced thread{totalMailboxThreads === 1 ? '' : 's'} total.</p>
          : <p>No threads yet. New mail arrives automatically via Gmail push — check <strong>Unsorted</strong> for inbox intake, or register push in Settings.</p>}
      </div>
    );
  }

  const openLinkPopover = (thread: ExecInboxThread, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuickMove(null);
    setLinkQuery('');
    setQuickLink({ thread, x: e.clientX, y: e.clientY });
  };

  return (
    <div className="exec-list-wrap">
      <div className="exec-list">
        {threads.map((t) => {
          const meta = stageMeta(t.stage);
          const draftPending = t.proposedDraft?.status === 'pending';
          const linkSuggested = !t.linkedTo && t.proposedLink?.status === 'pending';
          const people = threadRowPeople(t, ownerEmail, ownerName);
          const summary = displaySummary(t);
          const peopleTitle = [
            `From: ${people.sender.title || people.sender.label}`,
            people.to.length ? `To: ${people.to.join(', ')}` : '',
            people.cc.length ? `Cc: ${people.cc.join(', ')}` : '',
          ].filter(Boolean).join(' · ');

          return (
            <div
              key={t.id}
              className={`exec-row${t.unread ? ' unread' : ''}${selectedThreadId === t.threadId ? ' selected' : ''}`}
              onClick={() => onOpen(t)}
            >
              <span className="exec-row-dot-col">
                {t.unread && <span className="exec-row-unread-dot" />}
              </span>

              <div className="exec-row-body">
                <div className="exec-row-top">
                  <div className="exec-row-people" title={peopleTitle}>
                    <span
                      className={`exec-row-sender${people.sender.isYou ? ' exec-row-sender-you' : ''}`}
                    >
                      {people.sender.isYou && <CornerUpLeft size={11} aria-hidden />}
                      {people.sender.label}
                    </span>
                    {people.to.length > 0 && (
                      <span className="exec-row-recips">
                        <span className="exec-recip-label">To</span>
                        {people.to.map((name, i) => (
                          <span key={`to-${name}-${i}`} className="exec-recip-chip">{name}</span>
                        ))}
                      </span>
                    )}
                    {people.cc.length > 0 && (
                      <span className="exec-row-recips exec-row-recips-cc">
                        <span className="exec-recip-label">Cc</span>
                        {people.cc.map((name, i) => (
                          <span key={`cc-${name}-${i}`} className="exec-recip-chip">{name}</span>
                        ))}
                      </span>
                    )}
                  </div>

                  <button
                    className="exec-stage-pill"
                    style={{ ['--pill-bg' as any]: `${meta.accent}1A`, ['--pill-fg' as any]: meta.accent }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setQuickLink(null);
                      setQuickMove({ thread: t, x: e.clientX, y: e.clientY });
                    }}
                    title="Move to stage"
                  >
                    <span className="dot" style={{ background: meta.accent }} />
                    {meta.label}
                    <ChevronDown size={11} />
                  </button>

                  {t.linkedTo && (
                    <button
                      type="button"
                      className="exec-row-link-chip"
                      title={`Linked to ${t.linkedTo.type}: ${t.linkedTo.name} — click to change`}
                      onClick={(e) => openLinkPopover(t, e)}
                    >
                      <Link2 size={10} />
                      <span className="exec-row-link-chip-text">{t.linkedTo.name}</span>
                    </button>
                  )}
                  {linkSuggested && (
                    <span
                      className="exec-row-link-suggest"
                      title={t.proposedLink?.rationale || `Agent suggests linking to ${t.proposedLink?.target.name}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Sparkles size={10} />
                      {t.proposedLink!.target.name}
                      {typeof t.proposedLink?.confidence === 'number' && (
                        <em>{Math.round(t.proposedLink.confidence * 100)}%</em>
                      )}
                      <button
                        type="button"
                        className="accept"
                        title="Accept link"
                        onClick={(e) => { e.stopPropagation(); onAcceptSuggestion(t); }}
                      >
                        <Check size={11} />
                      </button>
                      <button
                        type="button"
                        className="dismiss"
                        title="Dismiss suggestion"
                        onClick={(e) => { e.stopPropagation(); onDismissSuggestion(t); }}
                      >
                        <X size={11} />
                      </button>
                    </span>
                  )}

                  <span className="exec-row-subject">{t.subject}</span>
                </div>

                <div className={`exec-row-summary${summary.pending ? ' exec-row-summary-pending' : ''}`}>
                  {!summary.pending && <Sparkles size={10} className="exec-row-summary-icon" aria-hidden />}
                  {summary.text}
                </div>
              </div>

              <span className="exec-row-meta">
                {!t.linkedTo && !linkSuggested && (
                  <button
                    type="button"
                    className="exec-row-linkbtn"
                    title="Link to a lead, deal or project"
                    aria-label="Link to a lead, deal or project"
                    onClick={(e) => openLinkPopover(t, e)}
                  >
                    <Link2 size={13} />
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    className="exec-row-delete"
                    title="Delete conversation"
                    aria-label="Delete conversation"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(t);
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
                {draftPending && <span className="exec-badge exec-badge-draft" title="Agent draft awaiting review"><FileText size={11} /> Draft</span>}
                {t.agent?.classifiedBy === 'agent' && !draftPending && <span className="exec-badge exec-badge-agent" title={t.agent?.rationale || 'Classified by agent'}><Sparkles size={11} /></span>}
                {t.hasAttachments && <Paperclip size={13} style={{ color: 'var(--pm-text-muted)' }} />}
                {t.messageCount > 1 && <span className="exec-row-time">{t.messageCount}</span>}
                {t.assignedTo && <span className="exec-badge-assignee" title="Assigned">{initials(t.assignedTo)}</span>}
                <span className="exec-row-time">{fmtTime(t.lastMessageAt)}</span>
              </span>
            </div>
          );
        })}
      </div>

      {quickMove && (
        <div
          className="exec-quickmove"
          style={{ position: 'fixed', top: Math.min(quickMove.y + 6, window.innerHeight - 340), left: Math.min(quickMove.x, window.innerWidth - 210) }}
          onClick={(e) => e.stopPropagation()}
        >
          {[{ id: EXEC_INBOX_UNSORTED_STAGE_ID, label: 'Unsorted', accent: '#94A3B8' },
            ...stages.filter((s) => !s.archived).sort((a, b) => a.order - b.order).map((s) => ({ id: s.id, label: s.label, accent: s.accent }))]
            .map((s) => (
              <button
                key={s.id}
                className={`exec-quickmove-item${quickMove.thread.stage === s.id ? ' current' : ''}`}
                onClick={() => { onMove(quickMove.thread, s.id); setQuickMove(null); }}
              >
                <span className="exec-stage-dot" style={{ background: s.accent }} />
                {s.label}
              </button>
            ))}
        </div>
      )}

      {quickLink && (
        <div
          className="exec-quickmove exec-quicklink"
          style={{ position: 'fixed', top: Math.min(quickLink.y + 6, window.innerHeight - 320), left: Math.min(quickLink.x, window.innerWidth - 260) }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            className="exec-link-input"
            placeholder="Link to a lead, deal or project…"
            value={linkQuery}
            autoFocus
            onChange={(e) => setLinkQuery(e.target.value)}
          />
          {filteredCandidates.map((c) => (
            <button
              key={`${c.type}-${c.id}`}
              className="exec-quickmove-item"
              onClick={() => { onLink(quickLink.thread, c); setQuickLink(null); setLinkQuery(''); }}
            >
              <span className="exec-link-type">{c.type}</span>
              {c.name}
            </button>
          ))}
          {!filteredCandidates.length && (
            <div className="exec-quicklink-empty">No matches</div>
          )}
          {quickLink.thread.linkedTo && (
            <button
              className="exec-quickmove-item exec-quicklink-remove"
              onClick={() => { onLink(quickLink.thread, null); setQuickLink(null); setLinkQuery(''); }}
            >
              <X size={12} /> Remove link
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExecInboxList;
